import type { FC, ReactNode } from 'react'
import type { ListingCard as ListingCardData, ListingPlaceholder } from '@/types/marketplace'

import Link from 'next/link'
import { BadgeCheck, Globe, MapPin, ShieldCheck, Star, Store } from 'lucide-react'

import { cn } from '@/lib/utils'
import { formatPrice, listingTypeLabel } from '@/lib/marketplace/format'
import { ListingPlaceholderArt } from '@/components/marketplace/listing-placeholder-art'
import { SaveListingButton } from '@/components/featured-listings/save-listing-button'

export interface ListingCardProps {
    /** Where the whole card links to. */
    href: string
    title: string
    /** Shown as the large price line. Already formatted for display. */
    price: string
    sellerName: string
    imageUrl?: string | null
    /**
     * The appearance a photo-less listing was given at enrichment time. Used
     * ONLY when `imageUrl` is null: an uploaded photo always wins.
     */
    placeholder?: ListingPlaceholder | null
    /** Chooses the theme when nothing was saved on the listing. */
    categorySlug?: string | null
    location?: string | null
    /** The small square label: For Sale, Service, Wanted, Job. */
    typeLabel?: string | null
    sellerVerified?: boolean
    negotiable?: boolean
    featured?: boolean
    sold?: boolean
    /** Id the save button saves against. Omit to hide the save button. */
    listingId?: string
    className?: string
    /** Only the first cards of a grid are eager: one of them can be LCP. */
    priority?: boolean
    /**
     * Which marketplace this listing belongs to, shown only where that is not
     * already obvious — the global browse page. A tenant storefront passes
     * nothing, because the page around the card already answers it.
     *
     * Rendered as a small chip on the image rather than a row in the card body:
     * the card is a fixed height at every breakpoint, so a new row would push
     * the grid out of alignment, and attribution is supporting information that
     * must not compete with the item itself.
     */
    community?: { name: string, href: string | null, logoUrl: string | null } | null
}

const isRemote = (location: string): boolean => /online|remote|worldwide|digital/i.test(location)

/**
 * The card's fixed outer height and its image band, declared once so the card,
 * the skeleton and the grid can never drift apart.
 */
const CARD_SHELL = 'relative h-[526px] w-full sm:h-[556px]'
const CARD_IMAGE = 'h-[230px] sm:h-[260px]'

/** The one grid shape every listing surface uses. */
export const LISTING_GRID_CLASS =
    'grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-5 lg:grid-cols-3 lg:gap-6 xl:grid-cols-4'

/**
 * THE listing card. Every marketplace grid in the app renders this component,
 * so the card markup exists exactly once: a white card on paper with a hairline
 * border, square labels, a soft shadow, and a red offset slab behind featured
 * listings.
 *
 * The card is a fixed height at every breakpoint and each row inside it owns a
 * fixed slot, so cards align perfectly across rows no matter how long the
 * title, price or place name is.
 *
 * Paid placement is always labelled on the card itself (spec section 28).
 */
export const ListingCard: FC<ListingCardProps> = ({
    href,
    title,
    price,
    sellerName,
    imageUrl,
    placeholder,
    categorySlug,
    location,
    typeLabel,
    sellerVerified = false,
    negotiable = false,
    featured = false,
    sold = false,
    listingId,
    className,
    priority,
    community
}): ReactNode => {
    const place = location?.trim() || ''
    const LocationIcon = place && isRemote(place) ? Globe : MapPin

    /**
     * The chip's contents, shared by the link and the plain form. The mark is
     * decorative (`alt=''`): the marketplace's name sits beside it as real
     * text, so announcing it twice is noise.
     */
    const attribution = community && (
        <>
            {community.logoUrl ? (
                // eslint-disable-next-line @next/next/no-img-element
                <img
                    src={community.logoUrl}
                    alt=''
                    width={36}
                    height={36}
                    loading='lazy'
                    decoding='async'
                    className='size-[18px] shrink-0 rounded-[4px] object-cover'
                />
            ) : (
                <Store className='size-[14px] shrink-0' aria-hidden='true' />
            )}

            <span className='truncate'>
                {community.name}
            </span>
        </>
    )

    return (
        <div className={cn(CARD_SHELL, className)}>
            {featured && !sold && (
                <div
                    aria-hidden='true'
                    className='absolute -bottom-[10px] -right-[8px] left-[10px] top-[10px] rounded-[16px] bg-brand-red'
                />
            )}

            <article
                className={cn(
                    'group relative flex h-full flex-col overflow-hidden rounded-[16px]',
                    'border border-ink-900/15 bg-paper text-ink-900',
                    'shadow-[0_18px_40px_rgba(0,0,0,0.13)]',
                    'transition-[transform,border-color,box-shadow] duration-[250ms] ease-out',
                    'focus-within:border-ink-900/40',
                    'lg:hover:border-ink-900/30 lg:hover:shadow-[0_22px_48px_rgba(0,0,0,0.16)]',
                    'motion-safe:lg:hover:-translate-y-[3px]'
                )}
            >
                <div className={cn('relative m-2 shrink-0 overflow-hidden rounded-[11px] bg-plate', CARD_IMAGE)}>
                    {imageUrl ? (
                        // eslint-disable-next-line @next/next/no-img-element
                        <img
                            src={imageUrl}
                            alt={title}
                            width={800}
                            height={600}
                            loading={priority ? 'eager' : 'lazy'}
                            fetchPriority={priority ? 'high' : 'auto'}
                            decoding='async'
                            sizes='(min-width: 1280px) 23vw, (min-width: 1024px) 31vw, (min-width: 640px) 46vw, 92vw'
                            className={cn(
                                'size-full object-cover transition-transform duration-500 ease-out',
                                'motion-safe:group-hover:scale-[1.025]',
                                sold && 'opacity-45'
                            )}
                        />
                    ) : (
                        <ListingPlaceholderArt
                            placeholder={placeholder}
                            categorySlug={categorySlug}
                            seed={listingId ?? href}
                            iconClassName='size-12 sm:size-14'
                        />
                    )}

                    <div className='pointer-events-none absolute left-3.5 top-3.5 flex flex-wrap gap-2'>
                        {sold && (
                            <span className='inline-flex items-center rounded-[5px] bg-ink-900 px-3 py-2 text-[11px] font-black uppercase leading-none tracking-[0.02em] text-white'>
                                Sold
                            </span>
                        )}

                        {featured && !sold && (
                            <span
                                title='Paid placement. This listing was promoted.'
                                className='inline-flex items-center rounded-[5px] bg-brand-red-ink px-3 py-2 text-[11px] font-black uppercase leading-none tracking-[0.02em] text-white'
                            >
                                Promoted
                            </span>
                        )}

                        {typeLabel && (
                            <span className='inline-flex items-center rounded-[5px] border border-ink-900/10 bg-white px-3 py-2 text-[11px] font-black uppercase leading-none tracking-[0.02em] text-ink-900 shadow-sm'>
                                {typeLabel}
                            </span>
                        )}
                    </div>

                    {community && (
                        <div className='absolute bottom-3.5 left-3.5 z-10 max-w-[calc(100%-72px)]'>
                            {community.href ? (
                                <Link
                                    href={community.href}
                                    title={`Browse ${community.name}`}
                                    className='mta-focus mta-scroll-safe flex min-h-6 items-center gap-1.5 rounded-[6px] border border-ink-900/10 bg-white/95 px-2 py-1 text-[11px] font-bold leading-none text-ink-900/80 shadow-sm backdrop-blur-sm transition-colors duration-200 hover:bg-ink-900 hover:text-white'
                                >
                                    {attribution}
                                    <span className='sr-only'>
                                        Open this marketplace
                                    </span>
                                </Link>
                            ) : (
                                <span className='flex min-h-6 items-center gap-1.5 rounded-[6px] border border-ink-900/10 bg-white/95 px-2 py-1 text-[11px] font-bold leading-none text-ink-900/80 shadow-sm'>
                                    {attribution}
                                </span>
                            )}
                        </div>
                    )}

                    {listingId && (
                        <SaveListingButton
                            listingId={listingId}
                            title={title}
                            className='bottom-3.5 right-3.5 size-[42px] rounded-[8px] border border-ink-900/10 bg-white text-ink-900 hover:bg-ink-900 hover:text-white'
                        />
                    )}
                </div>

                <div className='flex min-h-0 flex-1 flex-col px-5 pb-5 pt-[18px]'>
                    <p className='mb-2.5 flex h-[17px] min-w-0 items-center gap-2'>
                        <span className='truncate text-[13px] font-bold uppercase leading-[17px] tracking-[0.03em] text-ink-900/60'>
                            {sellerName}
                        </span>

                        {sellerVerified && (
                            <BadgeCheck
                                className='size-[17px] shrink-0 fill-brand-red text-white'
                                aria-label='Verified seller'
                            />
                        )}
                    </p>

                    <h3 className='line-clamp-2-fixed h-[52px] font-heading text-[20px] font-extrabold leading-[26px] tracking-[-0.025em] text-ink-900'>
                        <Link href={href} className='mta-focus mta-scroll-safe rounded-mta-xs'>
                            <span className='absolute inset-0' aria-hidden='true' />
                            {title}
                        </Link>
                    </h3>

                    <div className='mt-[18px] flex h-[29px] items-end justify-between gap-4'>
                        <p className='min-w-0 truncate font-heading text-[29px] font-black leading-none tracking-[-0.04em] text-ink-900'>
                            {price}
                        </p>

                        {negotiable && (
                            <span className='shrink-0 text-[15px] font-extrabold leading-none text-brand-red-ink'>
                                Negotiable
                            </span>
                        )}
                    </div>

                    <div aria-hidden='true' className='mb-[18px] mt-auto pt-[18px]'>
                        <span className='block h-px bg-ink-900/10' />
                    </div>

                    <div className='flex h-[18px] items-center justify-between gap-3 text-[14px] font-semibold leading-[18px] text-ink-900/75'>
                        {place ? (
                            <span className='flex min-w-0 items-center gap-2'>
                                <LocationIcon className='size-[18px] shrink-0' aria-hidden='true' />
                                <span className='truncate'>{place}</span>
                            </span>
                        ) : (
                            <span />
                        )}

                        {sellerVerified && (
                            <span className='flex shrink-0 items-center gap-2'>
                                <ShieldCheck className='size-[18px]' aria-hidden='true' />
                                Verified Seller
                            </span>
                        )}
                    </div>

                    {featured && !sold && (
                        <div className='mt-[18px] flex h-[37px] items-center justify-between gap-2 rounded-[8px] border border-brand-red/30 bg-brand-red/[0.06] px-3 text-[12px] font-bold leading-[15px] text-ink-900'>
                            <span className='truncate'>
                                <b className='text-brand-red-ink'>PROMOTED</b> paid placement
                            </span>

                            <Star className='size-[15px] shrink-0 fill-current' aria-hidden='true' />
                        </div>
                    )}
                </div>
            </article>
        </div>
    )
}

interface ListingCardItemProps {
    listing: ListingCardData
    className?: string
    priority?: boolean
    /**
     * The storefront this card is being rendered inside, from
     * `storefrontBasePath` in `@/lib/community/storefront`.
     *
     * Empty for the founding marketplace, whose listings live at the top-level
     * `/listing/...` route, so every existing grid is unchanged by this. A
     * tenant passes its own prefix, because `getListingBySlug` is scoped to one
     * community: the unprefixed route resolves the slug against the founding
     * tenant, so a tenant card pointing there is a 404, not a shortcut.
     */
    basePath?: string
}

/**
 * Adapter: maps a listing row from the database onto the card above. It holds
 * no markup of its own, so the card stays a single source of truth.
 */
export const ListingCardItem: FC<ListingCardItemProps> = ({
    listing,
    className,
    priority,
    basePath = ''
}): ReactNode => {
    const negotiable = listing.priceType === 'negotiable'

    /**
     * A card on the GLOBAL browse page links into the listing's OWN storefront,
     * not into the page it happens to be rendered on: `getListingBySlug` is
     * scoped to one community, so the unprefixed route resolves a slug against
     * the founding marketplace and a tenant's listing there is a 404. Inside a
     * storefront, `listing.community` is null and the page's own prefix wins,
     * which is what leaves every existing grid byte-identical.
     */
    const prefix = listing.community?.basePath ?? basePath

    return (
        <ListingCard
            href={`${prefix}/listing/${listing.slug}`}
            community={listing.community && {
                name: listing.community.name,
                href: listing.community.href,
                logoUrl: listing.community.logoUrl
            }}
            title={listing.title}
            price={formatPrice(
                listing.price,
                listing.currency,
                negotiable ? 'fixed' : listing.priceType
            )}
            sellerName={listing.sellerName}
            imageUrl={listing.imageUrl}
            placeholder={listing.placeholder}
            categorySlug={listing.categorySlug}
            location={listing.location}
            typeLabel={listingTypeLabel(listing.listingType)}
            sellerVerified={listing.sellerVerified}
            negotiable={negotiable}
            featured={listing.isFeatured}
            sold={listing.status === 'sold'}
            listingId={listing.id}
            className={className}
            priority={priority}
        />
    )
}

/**
 * The one grid. Every listing surface (home, browse, category, seller, related,
 * featured) renders this, so the columns, gaps and card sizing exist once.
 */
export const ListingGrid: FC<{
    listings: Array<ListingCardData>
    className?: string
    /** How many leading cards load eagerly. One of them is usually the LCP image. */
    priorityCount?: number
    /** The storefront these cards link within. Empty for the founding one. */
    basePath?: string
}> = ({ listings, className, priorityCount = 4, basePath = '' }): ReactNode => {
    return (
        <ul className={cn(LISTING_GRID_CLASS, className)}>
            {listings.map((listing, index) => (
                <li key={listing.id} className='min-w-0'>
                    <ListingCardItem
                        listing={listing}
                        priority={index < priorityCount}
                        basePath={basePath}
                    />
                </li>
            ))}
        </ul>
    )
}

/**
 * The loading placeholder for a listing grid. It mirrors the card's exact
 * outer height, image band and inner rows, so results do not reflow when the
 * real cards arrive.
 */
export const ListingSkeletonGrid: FC<{
    count?: number
    className?: string
}> = ({ count = 8, className }): ReactNode => {
    return (
        <div className={cn(LISTING_GRID_CLASS, className)} aria-hidden='true'>
            {Array.from({ length: count }).map((_, index) => (
                <div key={index} className={CARD_SHELL}>
                    <div className='flex h-full flex-col overflow-hidden rounded-[16px] border border-ink-900/15 bg-paper shadow-[0_18px_40px_rgba(0,0,0,0.13)]'>
                        <div className={cn('m-2 shrink-0 animate-pulse rounded-[11px] bg-plate', CARD_IMAGE)} />

                        <div className='flex min-h-0 flex-1 flex-col px-5 pb-5 pt-[18px]'>
                            <div className='mb-2.5 h-[17px] w-1/3 animate-pulse rounded-sm bg-plate' />

                            <div className='h-[52px] space-y-2'>
                                <div className='h-[18px] w-full animate-pulse rounded-sm bg-plate' />
                                <div className='h-[18px] w-3/5 animate-pulse rounded-sm bg-plate' />
                            </div>

                            <div className='mt-[18px] h-[29px] w-2/5 animate-pulse rounded-sm bg-plate' />

                            <div className='mb-[18px] mt-auto pt-[18px]'>
                                <span className='block h-px bg-ink-900/10' />
                            </div>

                            <div className='h-[18px] w-1/2 animate-pulse rounded-sm bg-plate' />
                        </div>
                    </div>
                </div>
            ))}
        </div>
    )
}
